import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import dayjs from 'dayjs';

import { CircularProgress, Accordion, AccordionSummary, AccordionDetails, Typography, IconButton, Box, Button, TextField, Switch, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Update, ExpandMore } from '@mui/icons-material';

import useErrorManager from "../../../hooks/useErrorManager";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

import GenericDetailsTable from '../../Layout/Table/GenericDetailsTable';
import TextLinkButton from "../../Layout/TextLinkButton"
import GenericDatePicker from '../../Layout/GenericDatePicker';

import { useNotification } from '../../../contexts/NotificationContext';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext'; // Import breadcrumb context

const ButtonsContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  width: 100%;
`;

const UpdateIconContainer = styled('div')`
    position: relative;  // Allows for absolute positioning of the icon
    display: inline-flex;
    align-items: center;  // Vertically center the text and icon
`;
const StyledUpdateIcon = styled(Update)`
    font-size: 1.5rem;  // Increase the icon size as needed
    position: absolute;  // Remove the icon from the document flow
    right: -24px;  // Adjust as necessary to control the space between the icon and text
    top: 50%;  // Center vertically
    transform: translateY(-50%);  // Center icon vertically relative to the text
`;

const Text = styled('span')`
    display: inline-block;  // Keep the text inline
`;


const EditableTextField = styled(TextField)`
    & .MuiInputBase-input {
        padding: 8px 8px;  /* Custom padding */
        font-size: revert;     /* Custom font size */
    }
`;

const StyledAutocomplete = styled(Autocomplete)`
& .MuiInputBase-input {
    padding: 8px 8px;  /* Custom padding */
    font-size: revert;     /* Custom font size */
}
`;


const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    width: 50%;
    max-width: 600px;  /* Ensure the dialog has a larger width */
  }
`;

const StyledDialogContent = styled(DialogContent)`
    display: flex;
    flex-direction: column;
    justify-content: center;  /* Center the content */
    align-items: flex-start;      /* Align the items in the center */
    gap: 16px;                /* Add spacing between elements */
    padding: 24px;            /* Add padding around content */
`;

const StyledDialogActions = styled(DialogActions)`
    display: flex;
    justify-content: space-between; /* Space out the buttons */
    padding: 16px;                  /* Add padding to the buttons */
    width: 100%;                    /* Ensure full width for button alignment */
`;


function GatewayInfo({ gatewayId, details, setDetails }) {

    const [locations, setLocations] = useState([]);
    const [editedDetails, setEditedDetails] = useState(details); 
    const [replacementDate, setReplacementDate] = useState(null); 

    const [editMode, setEditMode] = useState(false);     
    const [dialogOpen, setDialogOpen] = useState(false); 

    const { setError, clearError, getError } = useErrorManager();
    const { showNotification } = useNotification();
    
    const navigate = useNavigate();
    const axiosInstance = useAxiosPrivate();

    const detailsView = [
        { key: 'serial', label: 'Serial number' },
        { key: 'name', label: 'Description', render: (item) => editMode ? (
            <EditableTextField 
                value={editedDetails?.name || ''} 
                onChange={(e) => handleInputChange('name', e.target.value)} 
            />
        ) : (
          item.name
        ) }, // render: (value) => <Typography>{value}</Typography>
        { key: 'locationName', label: 'Location', render: (item) =>editMode ? (

                <StyledAutocomplete
                    options={locations}
                    getOptionLabel={(option) => option.name}
                    value={locations.find(loc => loc.id === editedDetails?.locationId) || null}
                    onChange={(event, newValue) => {
                        handleInputChange('locationId', newValue?.id || null);
                        handleInputChange('locationName', newValue?.name || 'No location assigned');
                    }}
                    renderInput={(params) => <TextField {...params} label="Location" variant="outlined" />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                />

            ) : (
                item.locationId ? (
                    <Box
                        sx={{
                        '& .MuiButton-root': { justifyContent: 'flex-start' },
                        }}
                    >
                        <TextLinkButton action={() => navigate(`/locations/${item.locationId}`)} text={item.locationName} />
                            
                    </Box>
                ) : 'No location assigned'
            )
        },
        { key: 'nextConnection', label: 'Next connection',  render: (item) => formatDateTime(item.nextConnection)  },
        { key: 'lastConnection', label: 'Last connection', render: (item) => formatDateTime(item.lastConnection)  },
        { key: 'handledNetworkCnt', label: 'Networks' },
        { key: 'type', label: 'Gateway type' },
        { key: 'mode', label: 'Mode' },
        { key: 'operationTime', label: 'Operating time', render: (item) => formatOperatingTime(item.operationTime) },
        { key: 'percentage', label: 'Battery level', render: (item) => 
            <>
            <UpdateIconContainer>
                <Text>
                    {item.percentage 
                        ? `${item.percentage}%${item.replacementDates?.length > 0 ? ` (Replaced: ${item.replacementDates[0]})` : ''}`
                        : "N/A"
                    }
                </Text>
                {/* IconButton with absolute positioning */}
                <IconButton onClick={handleDialogOpen} variant="contained" color="primary">
                    <StyledUpdateIcon />
                </IconButton>
            </UpdateIconContainer>
            {/* {item.replacementDates ? `${item.percentage}% (Replaced: ${item.replacementDates[0]})` : `${item.percentage}%`} 
            <StyledIconButton onClick={handleDialogOpen} variant="contained" color="primary" ><StyledUpdateIcon/></StyledIconButton> */}
            </>
        },
        { key: 'fwVersion', label: 'Firmware version' },
        { key: 'allowFirmwareUpdate', label: 'Automated firmware updates', render: (item) => 
            editMode ? (
                <Switch 
                    checked={editedDetails?.allowFirmwareUpdate || false} 
                    onChange={(e) => handleInputChange('allowFirmwareUpdate', e.target.checked)}
                    inputProps={{ 'aria-label': 'Allow Firmware Update' }}
                />
            ) : (
                item.allowFirmwareUpdate ? 'Yes' : 'No'
            )
        },
    ];

    
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await axiosInstance.get('/locations/list'); // Fetch locations from API
                setLocations(response.data); // Store the locations in state
                clearError('fetchLocations');
            } catch (error) {
                console.error('Error fetching locations:', error);
                showNotification('Error fetching locations!', 'error');
            }
        };

        fetchLocations();
    }, [gatewayId]);

    
    const formatOperatingTime = (operatingTimeRaw) => {

        if (!operatingTimeRaw){
            return "";
        }
        const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?/;
        const match = operatingTimeRaw.match(regex);
        
        const years = match[1] ? `${match[1]} year${match[1] > 1 ? 's' : ''}` : '';
        const months = match[2] ? `${match[2]} month${match[2] > 1 ? 's' : ''}` : '';
        const days = match[3] ? `${match[3]} day${match[3] > 1 ? 's' : ''}` : '';
    
        return [years, months, days].filter(Boolean).join(' ').trim();
    };


    const handleInputChange = (field, value) => {
        setEditedDetails(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const handleSave = async () => {
        try {
            await axiosInstance.put(`/gateways/${gatewayId}/edit`, editedDetails);
            setDetails(editedDetails);  // Update the details with the saved data
            setEditMode(false);         // Exit edit mode
        } catch (error) {
            console.error('Error saving gateway details:', error);
            setEditMode(false);
            showNotification('Failed to save gateway details', 'error');
        }
    };

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleDateChange = (newDate) => {
        setReplacementDate(newDate);
    };

    const formatDateForAPI = (date) => {
        return dayjs(date).format('DD.MM.YYYY');
    };

    const replaceBattery = async () => {

        if (!replacementDate || dayjs(replacementDate).isAfter(dayjs())){
            setDialogOpen(false);
            showNotification('Invalid Date!', 'error');
            return;
        } 

        try {
            const formattedDate = formatDateForAPI(replacementDate);
            await axiosInstance.post(`/gateways/${gatewayId}/batt`, { date: formattedDate });
            setDialogOpen(false);
            clearError('batt');
        } catch (error) {
            setReplacementDate(null);
            setDialogOpen(false);
            showNotification('Failed to replace the battery!', 'error');
        }
    };

    const formatDateTime = (isoString) => {

        if (!isoString){
            return "Unknown";
        }

        const date = new Date(isoString);
    
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
    
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
    
        return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
    };


    return (<>
                <Accordion defaultExpanded={true}> 
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h7">GENERAL INFORMATION</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <GenericDetailsTable
                            detailsValue={details}
                            detailsList={detailsView} />

                        <ButtonsContainer>
                        {/* <Button onClick={handleDialogOpen} variant="contained" color="primary">Replace Battery</Button> */}
                            {editMode ? (
                                <>
                                    <Button onClick={handleSave} variant="contained" color="primary">Save</Button>
                                    <Button 
                                        onClick={() => { setEditMode(false); setEditedDetails(details); }} 
                                        variant="contained" 
                                        color="secondary" 
                                        style={{ marginLeft: '10px' }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button onClick={() => setEditMode(true)} variant="contained" color="primary">Edit</Button>
                            )}

                        </ButtonsContainer>
                    </AccordionDetails>
                </Accordion>

                <StyledDialog open={dialogOpen} onClose={handleDialogClose}>
                    <DialogTitle>Battery Replacement</DialogTitle>
                    <StyledDialogContent>
                        <DialogContentText>
                            Please select the date for battery replacement.
                        </DialogContentText>

                        <GenericDatePicker
                            initialDate={replacementDate}
                            onDateChange={handleDateChange}
                        />                            

                    </StyledDialogContent>
                    <StyledDialogActions>
                        <Button onClick={handleDialogClose} color="secondary">Cancel</Button>
                        <Button onClick={replaceBattery} color="primary">Submit</Button>
                    </StyledDialogActions>
                </StyledDialog>
            </> 
    );
}

export default GatewayInfo;
